import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus, Upload, Trash2, Copy, CalendarRange, CloudUpload } from 'lucide-react';
import { deleteTournament, listTournamentSummaries, loadTournament, newId, saveTournament, turnierVorhanden, type TournamentSummary } from '@/store/db';
import { Button, Empty, Meldung, Pill, buttonClass, cx } from '@/ui';
import { merkeHinweis } from '@/lib/hinweis';
import { choiceDialog, confirmDialog } from '@/ui/dialogs';
import { fmtDate } from '@/lib/format';
import { parseBackup } from '@airmail/io';
import { createTournament, replay, type Tournament } from '@airmail/engine';
import { AccountMenu, CloudList } from '@/components/CloudList';
import { StufenMarke } from '@/components/ProMarke';
import { fehlerText } from '@/store/plan';
import { TourButton } from '@/tour/Tour';
import { useAuth } from '@/store/AuthProvider';
import { DEFAULT_API, deleteCloudTournament, enableSyncFor, listSyncIds, removeSync, syncOnce, type CloudTournament } from '@/store/sync';
import { FILTER, filterEintrag, gemerkterFilter, merkeFilter, statusLabel, type Filter } from '@/lib/turnierfilter';
import { useT } from '@/i18n';
import { Darstellung } from '@/components/Darstellung';
import { useTm } from '@/i18n/engine';
import { RechtLinks } from '@/layout/RechtLinks';

/*
 * Filter über der Liste: Alle · Geplant · Läuft · Beendet (lib/turnierfilter.ts,
 * gilt auch für „In der Cloud“). Die Wahl merkt sich der Browser; sie erscheint
 * erst, wenn die Liste es braucht (ab vier Turnieren oder sobald eins beendet
 * ist), sonst stünde über zwei Zeilen ein Bedienelement mit nichts zu bedienen.
 */

export function TournamentList() {
  // Nur die Zusammenfassungen — den vollen Stand holt erst, wer ihn braucht (Cloud, Duplizieren)
  const [rows, setRows] = useState<TournamentSummary[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [filter, setFilterState] = useState<Filter>(gemerkterFilter);
  const setFilter = (f: Filter) => {
    setFilterState(f);
    merkeFilter(f);
  };
  // Was im Konto liegt (CloudList meldet es) — die Zahlen im Filter zählen mit, was nicht auf dem Gerät ist
  const [cloud, setCloud] = useState<CloudTournament[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const nav = useNavigate();
  const auth = useAuth();
  const t = useT();
  // Meldungen aus dem Import und vom Server sind deutsch — übersetzt wird beim Anzeigen
  const tm = useTm();
  const [synced, setSynced] = useState<Set<string>>(new Set());
  const [cloudBusy, setCloudBusy] = useState<string | null>(null);

  const backfillBusy = useRef(false);
  // beides zusammen setzen — sonst sieht der Cloud-Abgleich kurz „alle Turniere ohne Sync“ und lädt sie doppelt hoch
  const refresh = async () => {
    const [r, s] = await Promise.all([listTournamentSummaries(), listSyncIds()]);
    setSynced(s);
    setRows(r);
  };
  const toCloud = async (row: TournamentSummary) => {
    setCloudBusy(row.id);
    try {
      const state = await loadTournament(row.id);
      if (!state) return await refresh();
      await enableSyncFor(state);
      await refresh();
    } catch (e) {
      // 402 (Stufe `frei`): dieselbe Erklärung wie am Gate statt „Nur in Standard"
      setMsg(t('In die Cloud sichern fehlgeschlagen: {e}', { e: tm(fehlerText(e)) }));
    } finally {
      setCloudBusy(null);
    }
  };
  useEffect(() => {
    refresh();
  }, []);
  // Angemeldet + online: lokale Turniere ohne Cloud-Sicherung nacheinander ins Konto sichern (docs/16)
  const userSub = auth.user?.sub;
  useEffect(() => {
    if (!userSub || !DEFAULT_API || !rows || !navigator.onLine) return;
    // „nicht in der Cloud" (aus der Cloud gelöscht, lokal behalten) bleibt draußen — bis jemand „In die Cloud" drückt
    const todo = rows.filter((r) => !synced.has(r.id) && r.cloud !== 'aus');
    if (todo.length === 0 || backfillBusy.current) return;
    backfillBusy.current = true;
    let alive = true;
    (async () => {
      try {
        for (const r of todo) {
          if (!alive) return;
          try {
            const state = await loadTournament(r.id);
            if (!state) continue;
            // anlegen und das Protokoll gleich hochladen — nicht erst beim nächsten Öffnen
            const { tournament, cfg } = await enableSyncFor(state);
            if (cfg.outbox.length > 0) await syncOnce(cfg, tournament);
          } catch (e) {
            console.warn('Cloud-Sicherung später', r.name, e);
            return;
          }
        }
      } finally {
        backfillBusy.current = false;
      }
      if (alive) await refresh();
    })();
    return () => {
      alive = false;
    };
  }, [userSub, rows, synced]);

  const importBackup = async (file: File) => {
    const r = parseBackup(await file.text());
    if (!r.ok) return setMsg(tm(r.error));
    let tn = r.tournament;
    // Die Hinweise erscheinen im Turnier (lib/hinweis.ts) — hier auf der Liste
    // standen sie nur einen Augenblick, dann ging es schon weiter
    let kopie = false;
    if (await turnierVorhanden(tn.id)) {
      // gleiche ID schon vorhanden → als Kopie mit neuer ID anlegen
      tn = withNewId(tn);
      kopie = true;
    }
    await saveTournament(tn);
    const texte = [...(kopie ? ['Turnier existierte schon — als Kopie „{name}“ importiert'] : []), ...r.warnings];
    merkeHinweis(texte.length ? { ton: 'hinweis', texte, turnier: tn.id, ...(kopie ? { werte: { name: tn.name } } : {}) } : null);
    nav(`/t/${tn.id}`);
  };

  const duplicate = async (row: TournamentSummary) => {
    // Vorlage: Stammdaten, Regeln, Felder, Phasen — ohne Teams und Ergebnisse (docs/07 §5)
    const src = await loadTournament(row.id);
    if (!src) return refresh();
    const keep = src.log.filter(
      (a) =>
        !a.type.startsWith('team.') &&
        !a.type.startsWith('match.') &&
        !a.type.startsWith('phase.draw') &&
        !a.type.startsWith('phase.freeze') &&
        !a.type.startsWith('phase.finish') &&
        a.type !== 'group.resolveLot' &&
        a.type !== 'tournament.setStatus',
    );
    const id = newId();
    const copy = replay({ ...src.init, id, name: `${src.name} (Kopie)`, createdAt: Date.now(), seed: id }, keep);
    copy.name = `${src.name} (Kopie)`;
    await saveTournament(copy);
    refresh();
  };

  /*
   * Löschen vom Gerät. Liegt das eigene Turnier auch in der Cloud, wird gefragt:
   * „Auch aus der Cloud löschen" oder „Nur auf diesem Gerät". Im zweiten Fall
   * geht das Sync-Profil mit — sonst käme das Turnier über ein verwaistes
   * Profil zurück; in der Cloud-Liste steht es dann als „nicht auf diesem
   * Gerät" und lässt sich absichtlich wieder holen. Beigetretene Turniere
   * (Helfer, Mitorganisator) kennen nur das lokale Löschen — die Cloud gehört
   * dort einem anderen.
   */
  const remove = async (row: TournamentSummary) => {
    const eigenes = auth.user && DEFAULT_API && (cloud ?? []).some((c) => c.id === row.id && c.role === 'owner');
    let auchCloud = false;
    if (eigenes) {
      const wahl = await choiceDialog(t('„{name}“ von diesem Gerät löschen? Das Turnier liegt auch in deiner Cloud. Vorher ein Backup ziehen, falls du es noch brauchst.', { name: row.name }), {
        wahl: [
          { key: 'geraet', label: t('Nur auf diesem Gerät') },
          { key: 'beides', label: t('Auch aus der Cloud löschen'), danger: true },
        ],
      });
      if (!wahl) return;
      auchCloud = wahl === 'beides';
    } else if (!(await confirmDialog(t('„{name}“ wirklich löschen? Vorher ein Backup ziehen, falls du es noch brauchst.', { name: row.name }), { ok: t('Löschen'), danger: true }))) return;
    try {
      if (auchCloud) await deleteCloudTournament(DEFAULT_API, row.id);
      // Das Profil geht immer mit: Ohne Turnier ist es nur noch ein Rest, der beim nächsten Holen ohnehin neu entsteht
      if (synced.has(row.id)) await removeSync(row.id);
      await deleteTournament(row.id);
    } catch (e) {
      setMsg(t('Löschen fehlgeschlagen: {e}', { e: tm(fehlerText(e)) }));
    }
    await refresh();
  };

  /*
   * Filter zeigen, sobald er etwas zu tun hat; die Zahlen je Knopf sagen, was
   * dahintersteckt — **eine** Zahl je Knopf für die ganze Seite: Turniere auf
   * dem Gerät plus die in der Cloud, die nicht hier liegen („Beendet 3“ = 2
   * hier + 1 nur im Konto). Cloud-Turniere ohne bekannten Stand zählen nur
   * unter „Alle“.
   */
  const lokal = new Map((rows ?? []).map((r) => [r.id, r.status]));
  const cloudNur = (cloud ?? []).filter((c) => !lokal.has(c.id));
  const zaehlung = FILTER.map((f) => (rows ?? []).filter((r) => f.trifft(r.status)).length + cloudNur.filter((c) => f.trifft(c.status)).length);
  const filterSichtbar = !!rows && (rows.length + cloudNur.length >= 4 || zaehlung[3]! > 0);
  const aktiv = filterEintrag(filter);
  const sichtbar = (rows ?? []).filter((r) => (filterSichtbar ? aktiv.trifft(r.status) : true));
  const cloudTrifft = filterSichtbar ? cloudNur.filter((c) => aktiv.trifft(c.status)).length : 0;

  return (
    /*
     * Volle Höhe als Spalte: Der Fuß (Impressum · Datenschutz · AGB) steht bei
     * kurzer Liste unten am Bildschirmrand, bei langer Liste unter dem Inhalt —
     * vorher klebte er bei zwei Turnieren mitten in der Seite.
     */
    <div className="min-h-full flex flex-col max-w-4xl w-full mx-auto p-4 md:p-8">
      <header className="mb-8 grid gap-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="w-8 h-8 bg-red [clip-path:polygon(16%_0,100%_0,84%_100%,0_100%)] shrink-0" />
          <div className="min-w-0">
            <h1 className="m-0 text-xl font-bold tracking-tight">Airmail</h1>
            <div className="label md:whitespace-nowrap">{t('Cornhole-Turniere · funktioniert offline')}</div>
          </div>
          <Darstellung className="ml-auto" />
        </div>
        <div className="flex items-center gap-2 flex-wrap border-t border-line2 pt-4">
          <Button variant="primary" onClick={() => nav('/neu')} data-tour="list-new">
            <Plus size={14} /> {t('Neues Turnier')}
          </Button>
          {/* Saisons stand hinter „Backup laden" und ging unter — jetzt gleich neben
              dem Hauptknopf und mit rotem Rahmen, aber nicht gefüllt: Zwei volle
              rote Knöpfe nebeneinander streiten sich um den Blick. */}
          <Link to="/saison" className={buttonClass('default', 'md', 'border-red text-red hover:bg-red/10')}>
            <CalendarRange size={14} /> {t('Saisons')}
          </Link>
          <Button onClick={() => fileRef.current?.click()}>
            <Upload size={14} /> {t('Backup laden')}
          </Button>
          <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={(e) => e.target.files?.[0] && importBackup(e.target.files[0])} />
          <TourButton />
          <span data-tour="list-account" className="ml-auto inline-flex items-center gap-2">
            {/* Stufe des Kontos (docs/21): „Pro · Test, 41 Tage", „Standard bis …", „Frei" */}
            <StufenMarke />
            <AccountMenu />
          </span>
        </div>
      </header>

      {/* Was hier noch steht, sind Fehler (Datei unlesbar, Cloud-Sicherung fehlgeschlagen) */}
      {msg && (
        <Meldung ton="fehler" onClose={() => setMsg(null)} className="mb-4">
          {msg}
        </Meldung>
      )}

      {filterSichtbar && (
        <div role="group" aria-label={t('Nach Status filtern')} className="inline-flex border border-line mb-3 max-w-full overflow-x-auto" data-list-filter>
          {FILTER.map((f, i) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={cx('label cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 max-md:min-h-11 whitespace-nowrap', filter === f.key ? 'bg-panel2 text-ink' : 'text-dim hover:text-ink')}
            >
              {t(f.label)}
              <span className={cx('font-mono text-[10px] tracking-normal', filter === f.key ? 'text-muted' : 'opacity-70')}>{zaehlung[i]}</span>
            </button>
          ))}
        </div>
      )}

      {rows === null ? (
        <div className="text-dim">{t('Lade …')}</div>
      ) : rows.length === 0 ? (
        <Empty title={t('Noch kein Turnier')}>
          {t('Leg dein erstes Turnier an — Name, Modus, Regeln, Felder, Teams. Dauert zwei Minuten.')}
          <div className="mt-4">
            <Button variant="primary" onClick={() => nav('/neu')}>
              <Plus size={14} /> {t('Neues Turnier')}
            </Button>
          </div>
        </Empty>
      ) : sichtbar.length === 0 ? (
        <Empty title={t(aktiv.leer ?? 'Noch kein Turnier')}>
          {/* Der Filter trifft nur unten in der Cloud — sagen, statt „keine“ zu behaupten */}
          {cloudTrifft > 0 && <div className="mb-3">{t('{n} in der Cloud — siehe unten', { n: cloudTrifft })}</div>}
          <Button size="sm" onClick={() => setFilter('alle')}>
            {t('Alle')} · {rows.length + cloudNur.length}
          </Button>
        </Empty>
      ) : (
        /* Am Handy (360 px) passen Name, Pille und Knöpfe nicht in eine Zeile — Status und Knöpfe rutschen unter den Namen */
        <ul className="m-0 p-0 list-none grid gap-3">
          {sichtbar.map((r) => (
            <li key={r.id} className="bg-panel2 border border-line flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3" data-turnier={r.id}>
              <Link to={`/t/${r.id}`} className="no-underline text-ink flex-1 min-w-[12rem]">
                <div className="font-bold tracking-tight truncate">{r.name}</div>
                <div className="font-mono text-[11px] text-dim">
                  {fmtDate(r.date) || t('ohne Datum')} · {t('{n} Teams', { n: r.teams })} · {t('{n} Felder', { n: r.courts })} · {r.competitionName}
                  {/* Aus der Cloud gelöscht, hier behalten: die Sicherung lässt es in Ruhe, bis jemand „In die Cloud" drückt */}
                  {auth.user && DEFAULT_API && r.cloud === 'aus' && !synced.has(r.id) && <span data-ohne-cloud> · {t('nicht in der Cloud')}</span>}
                </div>
              </Link>
              <span className="ml-auto flex items-center gap-3">
                <Pill tone={r.status === 'running' ? 'live' : r.status === 'finished' ? 'blue' : 'default'}>{t(statusLabel(r.status))}</Pill>
                {auth.user && DEFAULT_API && !synced.has(r.id) && (
                  <Button size="sm" title={t('Ins Konto sichern (Cloud-Sync einschalten)')} onClick={() => void toCloud(r)} disabled={cloudBusy === r.id}>
                    <CloudUpload size={14} /> {t('In die Cloud')}
                  </Button>
                )}
                <Button variant="ghost" size="sm" title={t('Als Vorlage duplizieren (ohne Teams)')} onClick={() => duplicate(r)}>
                  <Copy size={14} />
                </Button>
                <Button variant="ghost" size="sm" title={t('Löschen')} onClick={() => remove(r)}>
                  <Trash2 size={14} />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <CloudList
        lokal={lokal}
        filter={filterSichtbar ? filter : 'alle'}
        reloadKey={[...synced].sort().join(',') + '|' + (rows ?? []).map((r) => r.id).join(',')}
        onChanged={refresh}
        onList={setCloud}
      />
      {/* `mt-auto` schiebt den Hinweis samt Fuß ans untere Ende der Spalte */}
      <p className="text-xs text-dim mt-auto pt-8">
        {auth.user
          ? t('Turniere liegen auf diesem Gerät und — mit Cloud-Sync — gesichert in deinem Konto. Ohne Netz läuft alles weiter.')
          : t('Daten liegen nur in diesem Browser. Vor dem Turnier und nach jeder Phase ein Backup ziehen — oder als Organisator anmelden und Cloud-Sync nutzen.')}
      </p>
      <RechtLinks className="mt-3" />
    </div>
  );
}

function withNewId(t: Tournament): Tournament {
  const id = newId();
  const copy = replay({ ...t.init, id }, t.log);
  copy.name = `${t.name} (Import)`;
  return copy;
}

// `statusLabel` wohnt jetzt in lib/turnierfilter.ts (CloudList braucht es auch) — Start und Kopfzeile holen es weiter von hier
export { createTournament, statusLabel };
